import { PropsWithChildren, ReactElement, ReactNode } from 'react';

import { Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { SxProps } from '@mui/system';

import AmountCell, { AmountCellProps } from '../DataGrid/Cell/AmountCell';
import DateCell, { DateCellProps } from '../DataGrid/Cell/DateCell';
import FallbackCell, { defaultIsCellValueEmpty, FallbackCellProps } from '../DataGrid/Cell/FallbackCell';

interface ILabelValueFormatProps extends Omit<FallbackCellProps<ReactNode | Date>, 'children'> {
  format?: 'date' | 'amount';
  formatProps?: NonNullable<DateCellProps['formatProps']> | NonNullable<AmountCellProps['formatProps']>;
}

export interface ILabelValueProps extends ILabelValueFormatProps {
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  alignItems?: 'flex-start' | 'center' | 'baseline' | 'flex-end';
  justifyContent?: 'flex-start' | 'center' | 'flex-end';
  label: string;
  sx?: SxProps;
  labelSx?: SxProps;
}

const isEmptyString = (val: ReactNode): val is null | undefined =>
  typeof val === 'string' ? val.trim().length === 0 : defaultIsCellValueEmpty(val);

const renderFormattedValue = ({ format, formatProps, ...cellProps }: ILabelValueFormatProps): ReactElement => {
  if (format === 'amount') {
    return <AmountCell formatProps={formatProps as AmountCellProps['formatProps']} {...(cellProps as AmountCellProps)} />;
  }

  if (format === 'date') {
    return <DateCell formatProps={formatProps as DateCellProps['formatProps']} {...(cellProps as DateCellProps)} />;
  }

  return (
    <FallbackCell<ReactNode> isEmpty={isEmptyString} fallback="-" {...(cellProps as Omit<FallbackCellProps<ReactNode>, 'children'>)} />
  );
};

export default function LabelValue({
  children,
  label,
  labelSx,
  sx,
  direction = 'row',
  alignItems,
  justifyContent,
  ...props
}: PropsWithChildren<ILabelValueProps>) {
  const isRow = direction === 'row' || direction === 'row-reverse';

  return (
    <Grid
      sx={sx}
      direction={direction}
      alignItems={alignItems || (isRow ? 'center' : 'flex-start')}
      justifyContent={justifyContent}
      container
    >
      <Grid xs={isRow ? 6 : 12}>
        <Typography component="span" variant="body1" fontWeight={600} color="primary" noWrap sx={labelSx}>
          {label}
        </Typography>
      </Grid>
      <Grid xs={isRow ? 6 : 12}>{renderFormattedValue(props)}</Grid>
    </Grid>
  );
}
